import ArrowDown from '@eduzz/houston-icons/ArrowDown';
import ArrowUp from '@eduzz/houston-icons/ArrowUp';
import Table from '@eduzz/houston-ui/Table';
import CircularProgress from '@material-ui/core/CircularProgress';
import useTheme from '@material-ui/core/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import React, { memo, useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useObservable } from 'react-use-observable';
import Truncate from 'v2/components/Shared/TruncateText';
import { numberToBRL } from 'v2/helpers/money';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { financialStatementBlockedService } from 'v2/services/financialStatement/blocked';
import { financialStatementService } from 'v2/services/financialStatement/statement';

import type { IFinancialInvoice } from 'v2/interfaces/financial';
import type { User } from 'v2/interfaces/user';

import PaginationLoadMore from './PaginationLoadMore';
import { useStyles } from './styles';

interface IProps {
  transactionDate: string;
  showBlockedDetails?: boolean;
}

interface IInvoiceDetailsProps {
  invoiceId: number;
  className?: string;
  children: ReactNode;
  idPrefix: string;
  user: User;
}

function InvoiceDetailsLink({ invoiceId, className, idPrefix, children }: IInvoiceDetailsProps) {
  return (
    <Link
      to={`/producer/invoice/details/${invoiceId}`}
      id={`${idPrefix}-${invoiceId}`}
      className={className ? className : ''}
      target='_blank'
      rel='noopener noreferrer'
    >
      {children}
    </Link>
  );
}

function ContentInnerTable({ transactionDate, showBlockedDetails }: IProps) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  useObservable(
    () =>
      showBlockedDetails ? financialStatementBlockedService.loadDetails(transactionDate) : financialStatementService.loadDetails(transactionDate),
    []
  );

  const [currEntries, setCurrEntries] = useState(null);
  const [currPage, setCurrPage] = useState(1);

  const [isLoading] = useObservable(
    () => (showBlockedDetails ? financialStatementBlockedService.isLoadingDetails() : financialStatementService.isLoadingDetails()),
    []
  );
  const [pagination] = useObservable(
    () =>
      showBlockedDetails ? financialStatementBlockedService.getPaginationResponseDetails() : financialStatementService.getPaginationResponseDetails(),
    []
  );
  const [entries] = useObservable(
    () =>
      showBlockedDetails
        ? financialStatementBlockedService.getFinancialStatementBlockedDetails()
        : financialStatementService.getFinancialStatementDetails(),
    []
  );

  const handleUpdateEntries = useCallback(() => {
    if (!currEntries && !entries) {
      return setCurrPage(1);
    }

    if ((!currEntries && !!entries) || (!!pagination?.current_page && pagination?.current_page < currPage)) {
      setCurrEntries([...entries]);
      return setCurrPage(1);
    }

    if (!!pagination?.current_page && pagination?.current_page > currPage) {
      setCurrEntries([...currEntries, ...entries]);
      return setCurrPage(pagination?.current_page);
    }
  }, [currEntries, currPage, entries, pagination]);

  useEffect(() => {
    handleUpdateEntries();
  }, [handleUpdateEntries]);

  if (!currEntries) {
    return (
      <div className={classes.loader}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Table total={pagination?.total_items || 0}>
        <Table.Header>
          <Table.Column className={classes.headerCell} width={30} />
          <Table.Column className={classes.headerCell} width={40} align='center'>
            Fatura
          </Table.Column>
          <Table.Column className={classes.headerCell}>Histórico</Table.Column>
          <Table.Column className={classes.headerCell} width={150} align='right'>
            Valor
          </Table.Column>
        </Table.Header>
        <Table.Body>
          {currEntries?.map((entry: IFinancialInvoice, index: number) => {
            const isPositive = entry.amount > 0;
            const amountSign = isPositive && !showBlockedDetails ? '+' : '-';

            return (
              <Table.Row data={entry} index={index} key={`Row-${entry.invoice_id}-${index}`} className={classes.item}>
                <Table.Cell>{isPositive && !showBlockedDetails ? <ArrowDown className={classes.colorSuccess} /> : <ArrowUp />}</Table.Cell>
                <Table.Cell>
                  <div className={classes.InvoiceIdColumn}>
                    {entry.invoice_id ? (
                      <InvoiceDetailsLink user={user} invoiceId={entry.invoice_id} idPrefix='invoice'>
                        {entry.invoice_id}
                      </InvoiceDetailsLink>
                    ) : (
                      '-'
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <div className={classes.descriptionWrapper}>
                    {entry.invoice_id ? (
                      <InvoiceDetailsLink
                        user={user}
                        invoiceId={entry.invoice_id}
                        className={classes.descriptionLink}
                        idPrefix=' invoice-description'
                      >
                        <Truncate count={isMobile ? 25 : 70} final='...'>
                          {showBlockedDetails && entry.is_chargeback ? `[CHARGEBACK] - ${entry.description}` : entry.description}
                        </Truncate>
                      </InvoiceDetailsLink>
                    ) : (
                      <Truncate count={isMobile ? 25 : 70} final='...'>
                        {showBlockedDetails && entry.is_chargeback ? `[CHARGEBACK] - ${entry.description}` : entry.description}
                      </Truncate>
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell align={isMobile ? 'left' : 'right'}>
                  <span className={isPositive && !showBlockedDetails ? classes.colorSuccess : undefined}>
                    {`${amountSign} ${numberToBRL(isPositive ? entry.amount : entry.amount * -1)}`}
                  </span>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>

      {!!isLoading && (
        <div className={classes.loader}>
          <CircularProgress />
        </div>
      )}

      <PaginationLoadMore isLoading={isLoading} pagination={pagination} showBlockedDetails={showBlockedDetails} />
    </div>
  );
}

export default memo(ContentInnerTable);
